import { AfterViewInit, Component, Input } from '@angular/core';
import { BlockUI, NgBlockUI }              from 'ng-block-ui';
import { NgForm }                          from '@angular/forms';
import { TranslateService }                from '@ngx-translate/core';
import { UIRouter }                        from '@uirouter/core';

import { CodeTypeModel }             from '../core/models/code-type';
import { MessagesService }           from '../messages/messages.service';
import { PriceMatrixItemModel }      from './typings/price-matrix.item';
import { PriceMatrixModel }          from './typings/price-matrix.matrix';
import { PriceMatrixService }        from './price-matrix.service';
import { PriceMatrixUpdateModel }    from './typings/price-matrix.update-matrix';
import { RemoveComponent }           from '../table/cell-components/actions/remove.component';
import { Table }                     from '../table/types/table';
import { TableTextControlComponent } from '../table/cell-components/controls/text-control/table-text-control.component';

@Component({
  selector   : 'wor-price-matrix-settings',
  template: require('./price-matrix.settings.component.html')
})
export class PriceMatrixComponent implements AfterViewInit {
  @BlockUI('priceMatrixSettingsBlock') block: NgBlockUI;

  @Input() matrices   : Array<PriceMatrixModel>;
  @Input() types      : Array<CodeTypeModel>;
  @Input() updateTypes: Array<CodeTypeModel>;

  items: Array<PriceMatrixItemModel> = [];

  matrix = {} as PriceMatrixModel;

  tableSettings: Table = {
    columns: [
      {
        component: {
          bindings: {
            outputs: {
              onRemoveClick: ( item : PriceMatrixItemModel ) => this.deleteItem(item)
            }
          },
          enabled: true,
          factory: RemoveComponent,
        },
        sortable: false,
      },
      {
        field : 'product_group_name',
        header: {
          text: this.translateService.instant('PRODUCT_SPACE.PRODUCT_GROUP')
        }
      },
      {
        field : 'description',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
        }
      },
      {
        classes  : 'text-right',
        component: {
          bindings: {
            inputs: {
              model: 'percentage',
              name : 'percentage',
              type : 'number'
            }
          },
          enabled: true,
          factory: TableTextControlComponent
        },
        header: {
          classes: 'text-right',
          text   : this.translateService.instant('PRICE_MATRIX_SPACE.PERCENTAGE')
        }
      },
    ],
    empty: {
      text: this.translateService.instant('VEHICLE_SPACE.NO_TYPES_FOR_DESCRIPTION', {
        name: this.matrix ? this.matrix.matrix_name : ''
      })
    }
  };

  updateMatrix: PriceMatrixUpdateModel = {
    updateType: '*',
    percentage: 0
  };

  constructor (
    private messagesService   : MessagesService,
    public  priceMatrixService: PriceMatrixService,
    private router            : UIRouter,
    private translateService  : TranslateService
  ) {}

  ngAfterViewInit () : void {
    if ( this.matrices.length ) {
      this.matrix = Object.assign({}, this.matrices[ 0 ]);

      this.getItems();
    }
  }

  add () : void {
    this.priceMatrixService.add()
    .then(( matrix : PriceMatrixModel ) => this.priceMatrixService.get()
      .then(( matrices : Array<PriceMatrixModel> ) => {
        this.matrices = matrices;
        this.matrix   = matrix;
      }))
    .then(() => this.getItems())
    .catch(error => this.messagesService.error(error));
  }

  addAllProductGroups () : void {
    this.priceMatrixService.addAllProductGroups(this.matrix)
    .then(( items : Array<PriceMatrixItemModel> ) => this.items = items)
    .catch(error => this.messagesService.error(error));
  }

  addProductGroup () : void {
    this.priceMatrixService.addGroup(this.matrix)
    .then(( items : Array<PriceMatrixItemModel> ) => this.items = items)
    .catch(error => this.messagesService.error(error));
  }

  delete () : void {
    this.priceMatrixService.delete(this.matrix)
    .then(() => {
      this.messagesService.show(this.translateService.instant('PRICE_MATRIX_SPACE.MATRIX_DELETED', {
        code: this.matrix.matrix_name
      }), 'success');

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error));
  }

  deleteItem ( item : PriceMatrixItemModel ) : void {
    this.priceMatrixService.deleteItem(item)
    .then(( items : Array<PriceMatrixItemModel> ) => {
      this.messagesService.show(this.translateService.instant('PRICE_MATRIX_SPACE.ITEM_DELETED', {
        code: item.product_group_name
      }), 'success');

      this.items = items;
    })
    .catch(error => this.messagesService.error(error));
  }


  getItems () : void {
    this.tableSettings.empty.text = this.translateService.instant('VEHICLE_SPACE.NO_TYPES_FOR_DESCRIPTION', {
        name: this.matrix ? this.matrix.matrix_name : ''
      });

    if ( this.matrix.id ) {
      this.block.start();

      this.priceMatrixService.getItems(this.matrix)
      .then(( items : Array<PriceMatrixItemModel> ) => this.items = items)
      .catch(error => this.messagesService.error(error))
      .finally(() => this.block.stop());
    } else {
      this.items = [];
    }
  }

  onChange ( id : string ) : void {
    this.matrix = Object.assign({}, this.matrices.find(( matrix : PriceMatrixModel ) => matrix.id === id));

    this.getItems();
  }

  pushToOtherBranches () : void {
    this.priceMatrixService.update(this.matrix, this.items)
    .then(( data : { matrix : PriceMatrixModel; matrix_item : Array<PriceMatrixItemModel> } ) => {
      this.matrix = data.matrix;
      this.items  = data.matrix_item;

      return;
    })
    .then(() => this.priceMatrixService.pushToOtherBranches(this.matrix))
    .then(( message : string ) => this.messagesService.show(message, 'success'))
    .catch(error => this.messagesService.error(error));
  }

  save ( form : NgForm ) : void {
    this.block.start();

    this.priceMatrixService.update(this.matrix, this.items)
    .then(( data : { matrix : PriceMatrixModel; matrix_item : Array<PriceMatrixItemModel> } ) => {
      this.matrix = data.matrix;
      this.items  = data.matrix_item;
    })
    .catch(error => this.messagesService.error(error))
    .finally(() => this.block.stop());
  }

  updatePercentages () : void {
    this.priceMatrixService.updatePercentages(this.matrix, this.updateMatrix)
    .then(() => {
      this.messagesService.show(this.translateService.instant('PRICE_MATRIX_SPACE.PERCENTAGES_UPDATED'), 'success');

      this.getItems();
    })
    .catch(error => this.messagesService.error(error));
  }
}
